var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sseRefresh$, user$ } from '../stores/store-user.js';
import './components/element-dropdown.js';
import { filterDirty$, modal$, season$, updateChosenFilters$, userStatus$, } from '../stores/store-main.js';
import { filterDirtySvg, filterSvg, searchSvg, userDirtySvg, userSvg, } from '../helpers/elements.js';
import { getLanguageCode } from '../i18n.js';
import { resolveDeployPrefix } from '../initialization/deployPrefixResolver.js';
import { getOidcLoginEnabled, getURLSearchParams, } from '../urlHandling/urlSearchParams.js';
import { createNewTourURL, createSchmPlusInstructionsUrl, createSeasonURL, createToursURL, createTourURL, createUpdateAccountURL, } from '../urlHandling/urlCreate.js';
import { classMap } from 'lit/directives/class-map.js';
import { splitClassList } from '../helpers/classMap.js';
import { oidcLogin, oidcLogout } from '../helpers/oidcUser.js';
import { cookieLogin, cookieLogout } from '../helpers/cookieUser.js';
import { Router } from '@vaadin/router';
import { USER_ROLE } from '../helpers/helper-users.js';
import { hideOnOutsideClick } from '../helpers/dom.js';
import { monitorLoadingError } from '../initialization/obsoleteApi.js';
import { hasAuthCookie } from '../helpers/tokenOrCookie.js';
import { SSE_URL } from '../envs.js';
import { normalizeURL } from '../urlHandling/urlNormalizer.js';
import { edittrackMode$ } from '../stores/store-map.js';
import { SCREENS } from '../helpers/constants.js';
import { confirmDiscardChanges } from '../helpers/tours.js';
import { until } from 'lit/directives/until.js';
import { SubscriptionsController } from '../helpers/SubscriptionsController.js';
const deployPrefix = resolveDeployPrefix();
let ElementHeader = class ElementHeader extends LitElement {
    constructor() {
        super(...arguments);
        this.searchActive = false;
        this.filterPopupOpen = false;
        this.goToOldMapShown = false;
        this.goToOldMapOrLoginShown = false;
        this.displayLoginPanel = false;
        this.displayUserPanel = false;
        this.isTrackEditing = false;
        this.subscriptionController = new SubscriptionsController(this, () => [
            user$.subscribe((user) => {
                this.loggedIn = user !== null;
                this.userName = user?.email || user?.firstName;
                this.refreshUpdateTracksSSE(user?.userid);
            }),
            season$.subscribe((season) => (this.season = season)),
            filterDirty$.subscribe((set) => (this.filterDirty = set)),
            userStatus$.subscribe((value) => (this.userDirty = value)),
            updateChosenFilters$.subscribe((value) => {
                if (value)
                    this.showFilterPopup();
            }),
            modal$.subscribe((value) => {
                this.goToOldMapShown = value?.type === 'goToOldMap';
                this.goToOldMapOrLoginShown = value?.type === 'goToOldMapOrLogin';
                this.filterPopupOpen = value?.type === 'filter';
            }),
            edittrackMode$.subscribe((value) => (this.isTrackEditing = value === 'edit')),
        ]);
    }
    connectedCallback() {
        const urlParams = getURLSearchParams();
        if (urlParams.get('filters')) {
            this.showFilterPopup();
        }
        if (document.location.hostname === 'localhost') {
            this.resizeObserver = new ResizeObserver(() => {
                this.computeScreenSize();
            });
            this.resizeObserver.observe(this);
        }
        super.connectedCallback();
    }
    computeScreenSize() {
        const screenWidth = this.getBoundingClientRect().width;
        let screenCode = 'max';
        for (const [code, screenSize] of Object.entries(SCREENS)) {
            if (screenWidth >= screenSize) {
                screenCode = code;
            }
            else
                break;
        }
        this.currentScreenSize = screenCode;
    }
    disconnectedCallback() {
        this.resizeObserver.disconnect();
        super.disconnectedCallback();
    }
    async renderSearch() {
        if (this.filterPopup)
            this.filterPopup = undefined;
        await monitorLoadingError(import('./maps/map-element-search.js'));
        return html `
      <div class="h-header-zero sm:h-header-sm mdxx:h-header-mdxx"></div>
      <div
        class="absolute top-0 flex w-[100vw] items-center justify-center bg-white shadow"
      >
        <element-search
          class="contents"
          @close="${() => (this.searchActive = false)}"
        ></element-search>
      </div>
    `;
    }
    async showFilterPopup() {
        if (!this.filterPopup) {
            const { ElementFilterPopup } = await monitorLoadingError(import('./element-filter-popup.js'));
            this.filterPopup = new ElementFilterPopup();
            this.appendChild(this.filterPopup);
        }
        if (modal$?.value?.type !== 'filter') {
            await this.filterPopup.setChosenFilters();
            modal$.next({ type: 'filter' });
        }
        else if (modal$.value.type === 'filter' && this.filterPopupOpen) {
            modal$.next(undefined);
        }
    }
    async loginCallback() {
        if (getOidcLoginEnabled()) {
            await oidcLogin();
        }
        else {
            const lang = getLanguageCode();
            const linkLogin = `/${lang}/login`;
            localStorage.setItem('appPreLoginURL', document.location.href);
            Router.go(linkLogin);
        }
    }
    toggleUserPanel() {
        this.displayUserPanel = !this.displayUserPanel;
        if (this.displayUserPanel) {
            hideOnOutsideClick(() => [
                this.querySelector('.user-panel'),
                this.querySelector('.user-panel-btn'),
            ], () => (this.displayUserPanel = false));
        }
    }
    async cookieLogin(e) {
        e.preventDefault();
        const form = e.target;
        const data = {
            user: form['username'].value,
            password: form['password'].value,
        };
        this.displayLoginPanel = false;
        const loggedIn = await cookieLogin(data.user, data.password);
        const { kind, bag } = normalizeURL(document.location.toString(), getLanguageCode(), this.season, resolveDeployPrefix());
        if (loggedIn) {
            if (!['tour', 'recordedtrack'].includes(kind)) {
                Router.go(`${createToursURL(getLanguageCode())}`);
            }
            else {
                const url = bag.tourId
                    ? createTourURL(bag.tourId, kind === 'tour' ? 'normal' : 'recordedtrack', getLanguageCode())
                    : createNewTourURL(getLanguageCode());
                Router.go(url);
            }
            this.displayUserPanel = false;
        }
    }
    async cookieLogout() {
        this.displayUserPanel = false;
        if (this.isTrackEditing) {
            const discard = await confirmDiscardChanges();
            if (!discard) {
                return;
            }
        }
        edittrackMode$.next('');
        this.userName = undefined;
        await cookieLogout();
        Router.go(createSeasonURL(getLanguageCode(), this.season));
    }
    async oidcLogout() {
        this.displayUserPanel = false;
        if (this.isTrackEditing) {
            const discard = await confirmDiscardChanges();
            if (!discard) {
                return;
            }
        }
        edittrackMode$.next('');
        this.userName = undefined;
        await oidcLogout();
        Router.go(createSeasonURL(getLanguageCode(), this.season));
    }
    createNewEventSource(url) {
        console.log('SSE: creating source');
        this.sseEventSource = new EventSource(url);
        this.sseEventSource.addEventListener('message', (event) => {
            console.log(`SSE: ${event.data}`);
            sseRefresh$.next();
        });
        this.sseEventSource.addEventListener('error', () => {
            if (this.sseEventSource.readyState === 2) {
                const oldEvtSource = this.sseEventSource;
                const waitTime = 4000 + 30000 * Math.random();
                console.log(`SSE: error, list will not update for a ${Math.ceil(waitTime / 1000)}s`);
                setTimeout(() => {
                    if (this.sseEventSource === oldEvtSource &&
                        oldEvtSource.readyState === 2) {
                        console.log('Trying to resurect SSE');
                        this.createNewEventSource(oldEvtSource.url);
                    }
                }, waitTime);
            }
        });
    }
    refreshUpdateTracksSSE(id) {
        if (!SSE_URL) {
            console.log('Bypassing SSE registration');
            return;
        }
        const url = `${SSE_URL}events/${id}`;
        if (this.sseEventSource) {
            console.log(this.sseEventSource);
            if (this.sseEventSource.url === url &&
                this.sseEventSource.readyState !== 2) {
                console.log('SSE skipping recreating same SSE source');
                return;
            }
            console.log('SSE: closing source');
            this.sseEventSource.close();
        }
        if (id) {
            this.createNewEventSource(url);
        }
    }
    renderLoginPanel() {
        return html `<div
      class="login-panel absolute top-full right-[13%] inline bg-white p-5"
    >
      <form
        @submit="${(e) => this.cookieLogin(e)}"
        id="login-form"
        class="flex flex-col"
      >
        <input
          type="text"
          name="username"
          id="username"
          placeholder="username"
          class="mb-3 w-52 h-8"
          required
        />
        <input
          type="password"
          name="password"
          id="password"
          placeholder="password"
          class="mb-3 w-52 h-8"
          required
        />
      </form>
      <div class="flex justify-end">
        <button
          type="submit"
          class="button-color-red h-nav-button-s"
          form="login-form"
        >
          Login
        </button>
      </div>
    </div>`;
    }
    renderUserPanel() {
        const lang = getLanguageCode();
        return html `
      <div
        class="user-panel absolute ${classMap(splitClassList({
            visible: this.displayUserPanel,
            hidden: !this.displayUserPanel,
        }))} block top-[38px] sm:top-[56px] mdxx:top-[69px] left-[30px] sm:left-[56px] mdxx:-left-[24px] bg-white shadow-dropdown"
      >
        <div
          class="bg-chm-red hover:bg-chm-focus-red w-full h-[56px] md:h-[60px] px-[24px] cursor-pointer"
        >
          <div
            class="bg-[url('./src/img/logo_chm_plus_white.svg')] mt-[20px] md:mt-[23px] mdxx:mt-[15px] bg-no-repeat w-[60px] mdxx:w-[100px] h-logo-chm-plus-l align-middle inline-block"
          ></div>
        </div>
        <ul class="flex flex-col items-start pb-[16px] pt-[12px]">
          <li class="button-logged-nav">
            <a
              href="${createToursURL(getLanguageCode())}"
              @click="${() => (this.displayUserPanel = false)}"
              >I miei itinerari</a
            >
          </li>
          <li
            class="button-logged-nav"
            @click="${() => (this.displayUserPanel = false)}"
          >
            <a
              href="${createUpdateAccountURL(lang)}"
              target="_blank"
              @click="${() => {
            this.displayUserPanel = false;
        }}"
              >Gestire il mio account</a
            >
          </li>
          <li
            class="button-logged-nav"
            @click="${() => (this.displayUserPanel = false)}"
          >
            <a
              href="${createSchmPlusInstructionsUrl(getLanguageCode())}"
              @click="${() => (this.displayUserPanel = false)}"
              >Istruzioni</a
            >
          </li>
          <li class="button-logged-nav">
            <button
              data-cy="logout-btn"
              @click=${() => hasAuthCookie() ? this.cookieLogout() : this.oidcLogout()}
            >
              Disconnetersi
            </button>
          </li>
        </ul>
      </div>
    `;
    }
    renderScreenSizePin() {
        return html ` <div
      class="absolute top-0 right-0 h-[30px] w-[30px] md:w-[50px] bg-gray-200 z-[70] text-title flex justify-center items-center"
    >
      <p class="my-auto">${this.currentScreenSize}</p>
    </div>`;
    }
    renderHeader() {
        const languageCode = getLanguageCode();
        const filterIconClasses = `h-header-icon-s sm:h-header-icon-m mdxx:h-header-icon-l ${this.isTrackEditing
            ? ''
            : 'hover:fill-chm-focus-red active:fill-chm-focus-red'} ${this.filterPopupOpen ? 'fill-chm-focus-red' : ''}`;
        return html `
      ${document.location.hostname === 'localhost' ? this.renderScreenSizePin() : ''}
      <div class="flex justify-center h-header-zero sm:h-header-sm mdxx:h-header-mdxx bg-white shadow-header">
        <div class="flex items-center w-content">
          <a href="${createSeasonURL(languageCode, this.season)}" class="min-w-logo-chm-s sm:min-w-logo-chm-m mdxx:min-w-logo-chm-l">
            <img class="hidden sm:block h-logo-chm-s sm:h-logo-chm-m mdxx:h-logo-chm-l w-auto aspect-[187/30]"
                 src="${deployPrefix}/img/logo/e_schweizmobil_logo_${languageCode}.svg?ts11=TIMESTAMP11" alt="SvizzeraMobile">
            <img class="block sm:hidden h-logo-chm-s"
                 src="${deployPrefix}/img/logo/e_schweizmobil_logo.svg?ts11=TIMESTAMP11"
                 alt="logo" width="40" height="40">
          </a>
          <div class="hidden sm:block flex-1"></div>
          ${this.displayLoginPanel ? this.renderLoginPanel() : ''}
          ${this.userName
            ? html `
                  <div class="relative flex min-w-[50px]">
                    <button
                      class="user-panel-btn flex items-center mx-[50px] md:mx-[80px] mdxx:mr-[100px] mdxx:ml-0 overflow-hidden"
                      @click="${() => this.toggleUserPanel()}"
                    >
                      ${![
                USER_ROLE.subscribedUser,
                USER_ROLE.subscriptionPending,
            ].includes(this.userDirty)
                ? userDirtySvg(`h-header-icon-s sm:h-header-icon-m mdxx:h-header-icon-l min-w-header-icon-s sm:min-w-header-icon-m mdxx:min-w-header-icon-l mr-2 dirty-svg sm:mr-4 ${this.displayUserPanel
                    ? 'fill-chm-focus-red'
                    : ''}`, `${this.displayUserPanel ? 'fill-chm-focus-red' : 'point-red'}`)
                : userSvg(`h-header-icon-s sm:h-header-icon-m mdxx:h-header-icon-l min-w-header-icon-s sm:min-w-header-icon-m mdxx:min-w-header-icon-l hover:fill-chm-focus-red active:fill-chm-focus-red mr-2 sm:mr-4 ${this.displayUserPanel
                    ? 'fill-chm-focus-red'
                    : ''}`)}
                      <div
                        class="username-font whitespace-nowrap self-end text-ellipsis overflow-hidden"
                      >
                        ${this.userName}
                      </div>
                    </button>
                    ${this.renderUserPanel()}
                  </div>
                ` : html ` <button
                  data-cy="goToOldMap-btn"
                  @click=${() => this.loginCallback()}
                  class="login-panel-btn ml-[28px] sm:ml-0 mr-4 sm:mr-8 text-black sm:text-white h-full w-[74px] sm:w-[112px] mdxx:w-[184px] flex flex-col justify-center items-center ${classMap(splitClassList({
            'bg-white sm:bg-chm-red': !this.goToOldMapShown,
            'bg-chm-focus-red text-white': this.goToOldMapShown,
        }))}"
                >
                  <div
                    class="w-[60px] mdxx:w-[100px] bg-[url('./src/img/logo_chm_plus_red_black.svg')] sm:bg-[url('./src/img/logo_chm_plus_white.svg')] bg-no-repeat bg-center h-logo-chm-plus mdxx:h-logo-chm-plus-l sm:mb-[12px] ${classMap({
            "bg-[url('./src/img/logo_chm_plus_white.svg')]": this.goToOldMapShown,
        })}"
                  ></div>
                  <div class="link-with-icon-alone">
                    Login
                  </div>
                </button>`}
          <div class="block sm:hidden flex-1"></div>
          <button
                  @click="${() => {
            this.searchActive = true;
        }}" aria-label="Ricerca...">
            ${searchSvg(`${this.isTrackEditing ? '' : 'hover:fill-chm-focus-red'} h-header-icon-s sm:h-header-icon-m mdxx:h-header-icon-l`)}
          </button>
          <button class="ml-4 sm:ml-8 ${classMap({ 'opacity-50': this.isTrackEditing, 'cursor-not-allowed': this.isTrackEditing })}"
                  data-cy="filter-btn" data-cy-filer-set="${this.filterDirty}"
                  @click=${() => {
            if (this.isTrackEditing)
                return;
            this.showFilterPopup();
        }}
                  aria-label="Filtro">
            ${this.filterDirty
            ? filterDirtySvg(filterIconClasses)
            : filterSvg(filterIconClasses)}
          </button>
        </div>
      </div>
      </div>
    `;
    }
    render() {
        return this.searchActive
            ? until(this.renderSearch().then((res) => res))
            : this.renderHeader();
    }
    createRenderRoot() {
        return this;
    }
};
__decorate([
    state()
], ElementHeader.prototype, "loggedIn", void 0);
__decorate([
    state()
], ElementHeader.prototype, "searchActive", void 0);
__decorate([
    state()
], ElementHeader.prototype, "season", void 0);
__decorate([
    state()
], ElementHeader.prototype, "filterDirty", void 0);
__decorate([
    state()
], ElementHeader.prototype, "userDirty", void 0);
__decorate([
    state()
], ElementHeader.prototype, "filterPopupOpen", void 0);
__decorate([
    state()
], ElementHeader.prototype, "goToOldMapShown", void 0);
__decorate([
    state()
], ElementHeader.prototype, "goToOldMapOrLoginShown", void 0);
__decorate([
    state()
], ElementHeader.prototype, "displayLoginPanel", void 0);
__decorate([
    state()
], ElementHeader.prototype, "displayUserPanel", void 0);
__decorate([
    state()
], ElementHeader.prototype, "isTrackEditing", void 0);
__decorate([
    state()
], ElementHeader.prototype, "currentScreenSize", void 0);
ElementHeader = __decorate([
    customElement('element-header')
], ElementHeader);
export { ElementHeader };
//# sourceMappingURL=element-header.js.map